import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ENGINE,
  IMPORT_FILE_TYPES,
  defaultCheckedKeys,
  keysForPhase,
  todayIso,
} from '../config/eodPipeline';
import { xhrStream } from '../utils/xhrStream';
import EodConsole from '../components/eod/EodConsole';
import FileTypeGrid from '../components/eod/FileTypeGrid';
import PhaseScheduleBanner from '../components/eod/PhaseScheduleBanner';
import '../styles/eod-pipeline.css';

export default function EodImportPage() {
  const [fromDate, setFromDate] = useState(todayIso);
  const [toDate, setToDate] = useState(todayIso);
  const [selected, setSelected] = useState(() => new Set(defaultCheckedKeys(IMPORT_FILE_TYPES)));
  const [deleteExisting, setDeleteExisting] = useState(false);
  const [deleteFilesAfter, setDeleteFilesAfter] = useState(true);
  const [log, setLog] = useState('Ready...');
  const [barPct, setBarPct] = useState(0);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    document.body.classList.add('eod-pipeline-body');
    return () => document.body.classList.remove('eod-pipeline-body');
  }, []);

  const selectedKeys = useMemo(() => Array.from(selected), [selected]);

  const validateDates = useCallback(() => {
    if (!fromDate || !toDate) {
      alert('Please select From Date and To Date.');
      return false;
    }
    if (fromDate > toDate) {
      alert('From Date cannot be after To Date.');
      return false;
    }
    return true;
  }, [fromDate, toDate]);

  const toggleKey = useCallback((key) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const selectPhase = useCallback((phaseNum, checked) => {
    const keys = keysForPhase(IMPORT_FILE_TYPES, phaseNum);
    setSelected((prev) => {
      const next = new Set(prev);
      keys.forEach((k) => { if (checked) next.add(k); else next.delete(k); });
      return next;
    });
  }, []);

  const selectAll = useCallback(() => {
    const all = new Set();
    IMPORT_FILE_TYPES.forEach((s) => s.items.forEach((i) => all.add(i.value)));
    setSelected(all);
  }, []);

  const selectNone = useCallback(() => setSelected(new Set()), []);

  const runImport = useCallback(async () => {
    if (!validateDates()) return;
    if (selectedKeys.length === 0) {
      alert('Please select at least one data type to import.');
      return;
    }

    if (deleteExisting) {
      if (!window.confirm('⚠️ "Delete existing" is ON. This will delete and re-import data for the selected date range. Continue?')) return;
    } else if (!window.confirm(`Import ${selectedKeys.length} data type(s) for the selected date range?`)) {
      return;
    }

    setRunning(true);
    setLog('⏳ Starting import...\n');
    setBarPct(0);

    const fd = new FormData();
    fd.append('from_date', fromDate);
    fd.append('to_date', toDate);
    fd.append('selected_keys', JSON.stringify(selectedKeys));
    fd.append('delete_existing', deleteExisting ? '1' : '0');
    fd.append('delete_files_after', deleteFilesAfter ? '1' : '0');

    try {
      await xhrStream(`${ENGINE}process_import_all.php`, fd, {
        onLog: setLog,
        onProgress: setBarPct,
        barFn: 'setBar',
      });
      setBarPct(100);
    } catch (e) {
      setLog((l) => `${l}\n❌ Network error. Check console/server logs.`);
    }

    setRunning(false);
  }, [validateDates, selectedKeys, fromDate, toDate, deleteExisting, deleteFilesAfter]);

  const clearLog = useCallback(() => {
    setLog('Ready...');
    setBarPct(0);
  }, []);

  return (
    <div className="eod-pipeline-page">
      <div className="eod-sync-split">
        <div className="eod-sync-controls">
          <div className="eod-sync-controls-scroll">
            <div className="sync-controls-head">
              <h3><i className="bi bi-database-up" /> EOD Data Import</h3>
              <div className="sync-info">
                <div><b>Source:</b> Downloaded files from engine/downloads/</div>
                <div><b>Target:</b> MariaDB tables (upsert — duplicates auto-updated)</div>
              </div>
            </div>

            <PhaseScheduleBanner mode="import" />

            <div className="date-action-row">
              <div className="date-group">
                <label htmlFor="import-from">From Date</label>
                <input id="import-from" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} required />
              </div>
              <div className="date-group">
                <label htmlFor="import-to">To Date</label>
                <input id="import-to" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} required />
              </div>
              <div className="action-buttons">
                <button type="button" className="btnPrimary" onClick={runImport} disabled={running}>
                  {running ? <><span className="eod-spin">⏳</span> Importing...</> : <><i className="bi bi-database-up" /> Import Selected to DB</>}
                </button>
                <a href="/pages/manage_symbols.php" className="eod-link-btn">
                  <i className="bi bi-sliders2" /> Symbol Manager
                </a>
              </div>
            </div>

            <div className="option-row">
              <input
                type="checkbox"
                id="deleteExisting"
                checked={deleteExisting}
                onChange={(e) => setDeleteExisting(e.target.checked)}
              />
              <label htmlFor="deleteExisting">
                Delete existing rows before import <span className="hint">(recommended for re-import / data correction)</span>
              </label>
            </div>
            <div className="option-row">
              <input
                type="checkbox"
                id="deleteFilesAfter"
                checked={deleteFilesAfter}
                onChange={(e) => setDeleteFilesAfter(e.target.checked)}
              />
              <label htmlFor="deleteFilesAfter">
                Delete source files after successful import <span className="hint">(frees disk under engine/downloads)</span>
              </label>
            </div>

            <p className="control-hint" style={{ margin: '8px 0 12px', fontSize: 12 }}>
              <b>Daily auto-import (phased):</b> Runs in 3 phases shortly after downloads: Phase 1 ~16:40, Phase 2 ~16:55, Phase 3 ~19:00 IST (Mon–Fri). Skips non-trading days. Phase 3 imports participant OI/VOL for <a href="/participant">Participant Wise OI</a>.
            </p>

            <FileTypeGrid
              sections={IMPORT_FILE_TYPES}
              selected={selected}
              onToggle={toggleKey}
              onSelectPhase={selectPhase}
              onSelectAll={selectAll}
              onSelectNone={selectNone}
              metaClass="ft-table"
            />

            <div className="control-hint">
              Grouped by Recommended Phases (same as EOD Sync). Use Phase buttons for quick selection.
              Missing files for holidays are auto-skipped. Data is <b>upserted</b>.
            </div>

            <div className="progress-section">
              <div className="progress-wrap">
                <div className="progress-bar-fill bar-import" style={{ width: `${barPct}%` }}>{barPct}%</div>
              </div>
            </div>
          </div>
        </div>

        <EodConsole
          log={log}
          onClear={clearLog}
          subtitle="Streaming import output from PHP engine"
        />
      </div>
    </div>
  );
}